import { PlayerVitality, type VitalityState } from '../player/vitality'
import { WeaponInventory } from '../weapons/inventory'
import type { WeaponDefinition } from '../weapons/types'

export interface HUDOptions {
  container?: HTMLElement
}

export class GameHUD {
  readonly root: HTMLElement
  private healthValueEl!: HTMLElement
  private healthBarFillEl!: HTMLElement
  private healthTanksEl!: HTMLElement
  private energyValueEl!: HTMLElement
  private energyBarFillEl!: HTMLElement
  private energyTanksEl!: HTMLElement
  private reserveBadgeEl!: HTMLElement
  private damageVignetteEl!: HTMLElement
  private lowHealthVignetteEl!: HTMLElement

  private weaponNameEl!: HTMLElement
  private weaponSlotEl!: HTMLElement
  private weaponAmmoEl!: HTMLElement
  private weaponCostEl!: HTMLElement

  private lastHp = -1
  private lastHpMax = -1
  private lastHealthTanks = -1
  private lastEnergy = -1
  private lastEnergyMax = -1
  private lastEnergyTanks = -1
  private lastReserveTanks = -1
  private lastWeaponId = ''
  private lastAmmoCount = -1

  private damageFlashTimeout: ReturnType<typeof setTimeout> | null = null

  constructor(options: HUDOptions = {}) {
    const parent = options.container ?? document.body
    this.root = document.createElement('div')
    this.root.id = 'hud-container'
    this.buildDOM()
    parent.appendChild(this.root)
  }

  private buildDOM() {
    this.root.innerHTML = `
      <div id="damage-vignette" class="vignette damage-flash"></div>
      <div id="low-health-vignette" class="vignette low-health-pulse"></div>

      <!-- Left Vitality Panel -->
      <div class="hud-panel hud-vitality-left">
        <div class="hud-label-row">
          <span class="hud-title">VITALITY // HP</span>
          <span id="hud-hp-val" class="hud-value">100 / 100</span>
        </div>
        <div class="hud-bar-bg">
          <div id="hud-hp-bar" class="hud-bar-fill hp-fill" style="width: 100%;"></div>
        </div>
        <div id="hud-health-tanks" class="hud-tanks-row"></div>
      </div>

      <!-- Center Weapon Readout -->
      <div class="hud-panel hud-weapon-bottom">
        <div class="hud-label-row">
          <span id="hud-weapon-slot" class="hud-slot-badge">SLOT 1</span>
          <span id="hud-weapon-cost" class="hud-cost-badge">COST: 2 EN</span>
        </div>
        <div id="hud-weapon-name" class="hud-weapon-title">BASIC BLASTER</div>
        <div class="hud-ammo-row">
          <span class="hud-ammo-label">AMMO // CHARGE:</span>
          <span id="hud-weapon-ammo" class="hud-ammo-val">INFINITE</span>
        </div>
      </div>

      <!-- Right System / Energy Panel -->
      <div class="hud-panel hud-vitality-right">
        <div class="hud-label-row">
          <span class="hud-title">SYSTEM // ENERGY</span>
          <span id="hud-energy-val" class="hud-value">100 / 100</span>
        </div>
        <div class="hud-bar-bg">
          <div id="hud-energy-bar" class="hud-bar-fill energy-fill" style="width: 100%;"></div>
        </div>
        <div class="hud-tanks-footer">
          <div id="hud-energy-tanks" class="hud-tanks-row"></div>
          <div id="hud-reserve-badge" class="hud-reserve-badge hidden">RESERVE AUTO</div>
        </div>
        <div id="hud-keycards-row" class="hud-keycards-row"></div>
      </div>
    `

    this.healthValueEl = this.root.querySelector('#hud-hp-val') as HTMLElement
    this.healthBarFillEl = this.root.querySelector('#hud-hp-bar') as HTMLElement
    this.healthTanksEl = this.root.querySelector('#hud-health-tanks') as HTMLElement
    this.energyValueEl = this.root.querySelector('#hud-energy-val') as HTMLElement
    this.energyBarFillEl = this.root.querySelector('#hud-energy-bar') as HTMLElement
    this.energyTanksEl = this.root.querySelector('#hud-energy-tanks') as HTMLElement
    this.reserveBadgeEl = this.root.querySelector('#hud-reserve-badge') as HTMLElement
    this.damageVignetteEl = this.root.querySelector('#damage-vignette') as HTMLElement
    this.lowHealthVignetteEl = this.root.querySelector('#low-health-vignette') as HTMLElement

    this.weaponNameEl = this.root.querySelector('#hud-weapon-name') as HTMLElement
    this.weaponSlotEl = this.root.querySelector('#hud-weapon-slot') as HTMLElement
    this.weaponAmmoEl = this.root.querySelector('#hud-weapon-ammo') as HTMLElement
    this.weaponCostEl = this.root.querySelector('#hud-weapon-cost') as HTMLElement
  }

  attach(vitality: PlayerVitality): () => void {
    this.update(vitality.state)

    const unbindChange = vitality.onChange((state) => {
      this.update(state)
    })

    const unbindDamage = vitality.onDamage(() => {
      this.triggerDamageFlash()
    })

    return () => {
      unbindChange()
      unbindDamage()
    }
  }

  attachKeycards(keycards: { keycards: string[]; onChange: (cb: (cards: any[]) => void) => () => void }): () => void {
    const keycardsEl = this.root.querySelector('#hud-keycards-row') as HTMLElement
    const updateKeycards = (cards: string[]) => {
      if (!keycardsEl) return
      let html = ''
      for (const c of cards) {
        html += `<span class="keycard-badge card-${c}">KEYCARD ${c.toUpperCase()}</span>`
      }
      keycardsEl.innerHTML = html
    }

    updateKeycards(keycards.keycards)
    return keycards.onChange(updateKeycards)
  }

  attachWeapons(inventory: WeaponInventory, vitality: PlayerVitality): () => void {
    const updateWeaponDisplay = () => {
      this.updateWeapon(inventory.activeWeapon, inventory, vitality)
    }

    updateWeaponDisplay()
    const unbindWeapon = inventory.onWeaponChange(updateWeaponDisplay)
    const unbindFire = inventory.onFire(updateWeaponDisplay)
    const unbindAmmo = inventory.ammo.onChange(updateWeaponDisplay)
    const unbindVitality = vitality.onChange(updateWeaponDisplay)

    return () => {
      unbindWeapon()
      unbindFire()
      unbindAmmo()
      unbindVitality()
    }
  }

  updateWeapon(weapon: WeaponDefinition, inventory: WeaponInventory, vitality: PlayerVitality) {
    if (weapon.id !== this.lastWeaponId) {
      this.lastWeaponId = weapon.id
      this.lastAmmoCount = -1
      this.weaponNameEl.textContent = weapon.name
      this.weaponSlotEl.textContent = `SLOT ${weapon.slot} [${weapon.category.toUpperCase()}]`
      this.weaponNameEl.style.color = `#${weapon.color.toString(16).padStart(6, '0')}`

      if (weapon.ammoType === 'none') {
        this.weaponCostEl.textContent = 'FREE // 0 COST'
      } else if (weapon.ammoType === 'energy') {
        this.weaponCostEl.textContent = `-${weapon.ammoCost} ENERGY`
      } else {
        this.weaponCostEl.textContent = `-${weapon.ammoCost} ${weapon.ammoType.toUpperCase()}`
      }
    }

    let currentAmmo = 0
    if (weapon.ammoType === 'none') {
      if (this.lastAmmoCount !== -999) {
        this.lastAmmoCount = -999
        this.weaponAmmoEl.textContent = 'LAST RESORT (INF)'
      }
    } else if (weapon.ammoType === 'energy') {
      currentAmmo = Math.ceil(vitality.state.energy)
      if (currentAmmo !== this.lastAmmoCount) {
        this.lastAmmoCount = currentAmmo
        this.weaponAmmoEl.textContent = `${currentAmmo} EN`
      }
    } else {
      currentAmmo = inventory.ammo.getAmmo(weapon.ammoType)
      if (currentAmmo !== this.lastAmmoCount) {
        this.lastAmmoCount = currentAmmo
        this.weaponAmmoEl.textContent = `${currentAmmo} ROUNDS`
      }
    }
  }

  triggerDamageFlash() {
    this.damageVignetteEl.classList.add('active')
    if (this.damageFlashTimeout) clearTimeout(this.damageFlashTimeout)
    this.damageFlashTimeout = setTimeout(() => {
      this.damageVignetteEl.classList.remove('active')
    }, 180)
  }

  update(state: VitalityState) {
    // Health update
    if (state.hp !== this.lastHp || state.hpMax !== this.lastHpMax) {
      this.lastHp = state.hp
      this.lastHpMax = state.hpMax
      this.healthValueEl.textContent = `${Math.ceil(state.hp)} / ${state.hpMax}`
      const pct = Math.max(0, Math.min(100, (state.hp / state.hpMax) * 100))
      this.healthBarFillEl.style.width = `${pct}%`

      if (state.hp > 0 && state.hp / state.hpMax < 0.25) {
        this.lowHealthVignetteEl.classList.add('active')
      } else {
        this.lowHealthVignetteEl.classList.remove('active')
      }
    }

    // Health tanks update
    if (state.healthTanks !== this.lastHealthTanks) {
      this.lastHealthTanks = state.healthTanks
      let html = ''
      for (let i = 0; i < state.healthTanks; i++) {
        html += '<span class="tank-pip hp-pip"></span>'
      }
      this.healthTanksEl.innerHTML = html
    }

    // Energy update
    if (state.energy !== this.lastEnergy || state.energyMax !== this.lastEnergyMax) {
      this.lastEnergy = state.energy
      this.lastEnergyMax = state.energyMax
      this.energyValueEl.textContent = `${Math.ceil(state.energy)} / ${state.energyMax}`
      const pct = Math.max(0, Math.min(100, (state.energy / state.energyMax) * 100))
      this.energyBarFillEl.style.width = `${pct}%`
    }

    // Energy tanks update
    if (state.energyTanks !== this.lastEnergyTanks) {
      this.lastEnergyTanks = state.energyTanks
      let html = ''
      for (let i = 0; i < state.energyTanks; i++) {
        html += '<span class="tank-pip energy-pip"></span>'
      }
      this.energyTanksEl.innerHTML = html
    }

    // Reserve badge update
    if (state.reserveTanks !== this.lastReserveTanks) {
      this.lastReserveTanks = state.reserveTanks
      if (state.reserveTanks > 0) {
        this.reserveBadgeEl.textContent = `RESERVE x${state.reserveTanks}`
        this.reserveBadgeEl.classList.remove('hidden')
      } else {
        this.reserveBadgeEl.classList.add('hidden')
      }
    }
  }

  destroy() {
    if (this.damageFlashTimeout) clearTimeout(this.damageFlashTimeout)
    this.root.remove()
  }
}
